import { useQueryClient } from '@tanstack/react-query';
import { getAPIErrorCode, queryUtils, useDeleteCollectionMutation, useRenameCollectionMutation } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { ACTIVE_PDP_DELETE_REASONS, ACTIVE_PDP_ERROR_CODES } from '@vizit/constants';
import * as React from 'react';
import { refreshBenchmarkCollections } from './refresh-benchmark';

interface UseCollectionMenuActionsOptions {
  /**
   * Portfolio ID for query invalidation
   */
  workspaceId?: string;
  /**
   * Optional callback to navigate after deletion
   * If provided, will be called after successful delete
   */
  onNavigateAfterDelete?: () => void | Promise<void>;
}

interface UseCollectionMenuActionsReturn {
  /**
   * Handler for renaming a collection
   * @param collectionId - The ID of the collection to rename
   * @param newName - The new name for the collection
   */
  handleRename: (collectionId: string, newName: string) => Promise<void>;
  /**
   * Handler for deleting a collection
   * @param collectionId - The ID of the collection to delete
   */
  handleDelete: (collectionId: string) => Promise<void>;
}

/**
 * Hook that provides handlers for collection menu actions (rename, delete)
 * Encapsulates toast notifications, query invalidation, and navigation logic
 *
 * @param options - Configuration options
 * @returns Handlers for collection menu actions
 *
 * @example
 * ```tsx
 * const { handleRename, handleDelete } = useCollectionMenuActions({
 *   workspaceId,
 *   onNavigateAfterDelete: () => router.navigate({ to: '/library/collections' }),
 * });
 *
 * <CollectionMenuDropdown
 *   collectionId={id}
 *   onSuccessfulRename={handleRename}
 *   onSuccessfulDelete={handleDelete}
 * />
 * ```
 */
export function useCollectionMenuActions(options: UseCollectionMenuActionsOptions): UseCollectionMenuActionsReturn {
  const { workspaceId, onNavigateAfterDelete } = options;
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const renameMutation = useRenameCollectionMutation();
  const deleteMutation = useDeleteCollectionMutation();

  const handleRename = React.useCallback(
    async (collectionId: string, newName: string) => {
      if (!workspaceId) {
        addToast({
          title: 'Error',
          description: 'Portfolio ID is required to rename a collection',
          level: AlertLevel.Error,
        });
        return;
      }

      try {
        await renameMutation.mutateAsync({
          workspace_id: workspaceId,
          collection_id: collectionId,
          name: newName,
        });

        addToast({
          title: 'Collection renamed',
          description: `Collection renamed to "${newName}"`,
          level: AlertLevel.Success,
        });

        // Invalidate all collection lists and the specific collection detail
        void queryUtils.invalidateAllCollectionLists(queryClient, workspaceId);
        void queryUtils.invalidateCollectionDetail(queryClient, workspaceId, collectionId);
      } catch (error) {
        addToast({
          title: 'Error',
          description: 'Failed to rename collection',
          level: AlertLevel.Error,
        });
      }
    },
    [workspaceId, queryClient, addToast, renameMutation],
  );

  const handleDelete = React.useCallback(
    async (collectionId: string) => {
      if (!workspaceId) {
        addToast({
          title: 'Error',
          description: 'Portfolio ID is required to delete a collection',
          level: AlertLevel.Error,
        });
        return;
      }

      try {
        await deleteMutation.mutateAsync({
          workspace_id: workspaceId,
          collection_id: collectionId,
        });

        addToast({
          title: 'Collection deleted',
          description: 'Collection has been deleted',
          level: AlertLevel.Success,
        });

        // Invalidate all collection lists (fire-and-forget)
        void queryUtils.invalidateAllCollectionLists(queryClient, workspaceId);
        // A benchmark it was scored against counts one collection fewer.
        void refreshBenchmarkCollections(queryClient, workspaceId);

        // Navigate if callback provided (e.g., from details page)
        if (onNavigateAfterDelete) {
          await onNavigateAfterDelete();
        }
      } catch (error) {
        // The API refuses to delete a collection while one of its images, or a Spark variant made from one, is hero or
        // carousel on an active product page.
        if (getAPIErrorCode(error) === ACTIVE_PDP_ERROR_CODES.collection) {
          addToast({
            title: 'Could not delete the collection',
            description: `${ACTIVE_PDP_DELETE_REASONS.collection}.`,
            level: AlertLevel.Error,
          });
          return;
        }

        addToast({
          title: 'Error',
          description: 'Failed to delete collection',
          level: AlertLevel.Error,
        });
      }
    },
    [workspaceId, queryClient, addToast, deleteMutation, onNavigateAfterDelete],
  );

  return {
    handleRename,
    handleDelete,
  };
}
